import { Box, HStack, Stack, Tag, Text } from "@chakra-ui/react";
import { formatMoneyCents, Money } from "../../ui/Money";
import { BillingLine } from "./types";

const KIND_LABEL: Record<string, string> = {
  service: "Service",
  product: "Product",
  form: "Form",
};

type Props = {
  lines: BillingLine[];
};

function CoverageChips({ line }: { line: BillingLine }) {
  const chips: { label: string; bg: string; color: string }[] = [];
  if (line.coveredByInsuranceCents > 0) {
    chips.push({
      label: `${formatMoneyCents(line.coveredByInsuranceCents)} insurance`,
      bg: "#eaecf0",
      color: "#475467",
    });
  }
  if (line.coveredByMembershipCents > 0) {
    chips.push({
      label: `${formatMoneyCents(line.coveredByMembershipCents)} membership`,
      bg: "#d1fadf",
      color: "#027a48",
    });
  }
  if (line.yourPortionCents > 0) {
    chips.push({
      label: `${formatMoneyCents(line.yourPortionCents)} you`,
      bg: "#fef0c7",
      color: "#b54708",
    });
  }
  if (chips.length === 0) return null;
  return (
    <HStack spacing={1.5} flexWrap="wrap" mt={1}>
      {chips.map((c) => (
        <Tag
          key={c.label}
          size="sm"
          bg={c.bg}
          color={c.color}
          borderRadius="full"
          px={2}
          py={0}
          fontSize="11px"
        >
          {c.label}
        </Tag>
      ))}
    </HStack>
  );
}

export const LinesBreakdown = ({ lines }: Props) => {
  if (!lines || lines.length === 0) return null;
  return (
    <Box mt={3}>
      <Text
        fontSize="11px"
        color="#6b7280"
        textTransform="uppercase"
        letterSpacing="0.04em"
        fontWeight="600"
        mb={2}
      >
        Breakdown
      </Text>
      <Stack
        spacing={0}
        borderWidth="1px"
        borderColor="#f0f0f0"
        borderRadius="8px"
        overflow="hidden"
      >
        {lines.map((line) => (
          <HStack
            key={line.lineId}
            align="flex-start"
            spacing={3}
            px={3}
            py={2.5}
            borderBottomWidth="1px"
            borderColor="#f0f0f0"
            _last={{ borderBottomWidth: 0 }}
            bg="white"
          >
            <Box flex={1} minW={0}>
              <HStack spacing={2} align="center">
                <Text fontSize="13px" color="#1a1f36" fontWeight="500" noOfLines={1}>
                  {line.title}
                </Text>
                <Tag
                  size="sm"
                  bg="#f8fafc"
                  color="#475467"
                  borderRadius="full"
                  px={2}
                  py={0}
                  fontSize="11px"
                >
                  {KIND_LABEL[line.kind] || line.kind}
                </Tag>
                {line.quantity > 1 && (
                  <Text fontSize="11px" color="#6b7280">
                    × {line.quantity}
                  </Text>
                )}
              </HStack>
              <CoverageChips line={line} />
            </Box>
            <Box textAlign="right">
              <Money
                cents={line.totalCents}
                fontSize="13px"
                fontWeight="600"
                color="#1a1f36"
              />
            </Box>
          </HStack>
        ))}
      </Stack>
    </Box>
  );
};
